import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { DownloadIcon } from "lucide-react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip
} from "chart.js";
import { Line } from "react-chartjs-2";
import styles from "./LatencyTrendCard.module.css";
import TCUStyles from "../../TCU.module.css";
import { useLatencyContextData } from "../context/Context";
import { formatDuration } from "../deviceStates";
import { LATENCY_DOWNLOAD_APIS, downloadCsv, getLatencyTrend } from "../LatencyService";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);

const LINE_COLOR = "#4b71a2";
const GRID_COLOR = "#e3e8ee";
const TICK_COLOR = "#6b7280";

// y-axis scales to the data: starts at 0, ~10% headroom above the highest point
const Y_HEADROOM = 1.1;
const formatYTick = (value) => `${Number(value.toFixed(1))}s`;

// Min view: one tab per hour of the day ("12 AM" … "11 PM")
const HOURS = Array.from({ length: 24 }, (_, h) => ({
    value: h,
    label: moment({ hour: h }).format("h A")
}));

// "13:05" -> "1:05 PM"
const toClock = (label) => {
    const time = moment(label, "HH:mm", true);
    return time.isValid() ? time.format("h:mm A") : label;
};

// key = API graphType
const GRANULARITIES = [
    { key: "minute", label: "Min", axis: "Minute" },
    { key: "hour", label: "Hour", axis: "Hour" },
    { key: "day", label: "Day", axis: "Day" }
];

// vertical guide at the hovered point
const crosshairPlugin = {
    id: "latencyCrosshair",
    afterDatasetsDraw(chart) {
        const active = chart.tooltip?.getActiveElements?.();
        if (!active?.length) return;

        const { ctx, chartArea } = chart;
        const x = active[0].element.x;

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.lineWidth = 1;
        ctx.strokeStyle = "#a9bfd4";
        ctx.setLineDash([4, 4]);
        ctx.stroke();
        ctx.restore();
    }
};

function ChartLoader() {
    return (
        <div className={styles.chartState}>
            <span className={styles.spinner} />
            <span>Loading...</span>
        </div>
    );
}

export default function LatencyTrendCard({
    title = "Latency trend",
    subtitle = "Overall L1 → L3"
}) {
    const { GlobalFilter } = useLatencyContextData();
    const [granularity, setGranularity] = useState("minute");
    const [selectedHour, setSelectedHour] = useState(0); // 12 AM
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const hourTabsRef = useRef(null);

    const isMinView = granularity === "minute";

    // the Device Health switches are not part of the trend; leaving them out also keeps a
    // switch flip from refetching this chart
    const { ignitionOn, emergencyStatus, backupBatteryStatus, ...trendFilter } = GlobalFilter || {};
    const filterKey = JSON.stringify(trendFilter);

    // hours after the current one have no data yet when the filter date is today
    const lastHour = moment(GlobalFilter?.endDate, "YYYY-MM-DD").isSame(moment(), "day")
        ? moment().hour()
        : 23;

    // keep the selected hour valid when the filter date changes
    useEffect(() => {
        setSelectedHour((h) => Math.min(h, lastHour));
    }, [lastHour]);

    // bring the active hour tab into view - scrolls only the tab strip, never the page
    useEffect(() => {
        const strip = hourTabsRef.current;
        const tab = strip?.querySelector('[aria-selected="true"]');
        if (!strip || !tab) return;

        if (tab.offsetLeft < strip.scrollLeft) {
            strip.scrollLeft = tab.offsetLeft;
        } else if (tab.offsetLeft + tab.offsetWidth > strip.scrollLeft + strip.clientWidth) {
            strip.scrollLeft = tab.offsetLeft + tab.offsetWidth - strip.clientWidth;
        }
    }, [selectedHour, isMinView]);

    // left / right arrows move between hour tabs
    const onHourKeyDown = (e) => {
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
        e.preventDefault();
        const next = selectedHour + (e.key === "ArrowRight" ? 1 : -1);
        if (next >= 0 && next <= lastHour) setSelectedHour(next);
    };

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);

        // minute view sends the selected hour as "00" … "23"
        getLatencyTrend({
            ...JSON.parse(filterKey),
            graphType: granularity,
            ...(isMinView ? { hour: String(selectedHour).padStart(2, "0") } : {})
        })
            .then((res) => {
                if (!cancelled) setRows(res?.data || []);
            })
            .catch(() => {
                if (!cancelled) setRows([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [filterKey, granularity, isMinView, selectedHour]);

    const active = GRANULARITIES.find((g) => g.key === granularity);

    const data = useMemo(() => ({
        labels: rows.map((r) => r.label),
        datasets: [
            {
                label: "Avg interval",
                data: rows.map((r) => r.avgInterval ?? null),
                borderColor: LINE_COLOR,
                backgroundColor: LINE_COLOR,
                borderWidth: 2,
                tension: 0.35,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBorderColor: "#ffffff",
                pointBorderWidth: 2,
                pointHoverBorderWidth: 2
            }
        ]
    }), [rows]);

    // highest plotted value, for the y-axis headroom
    const yMax = useMemo(
        () => Math.max(0, ...rows.map((r) => Number(r.avgInterval) || 0)),
        [rows]
    );

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 300 },
        interaction: { mode: "index", intersect: false },
        layout: { padding: { top: 6, right: 8 } },
        plugins: {
            legend: { display: false },
            // ChartDataLabels is registered globally elsewhere; values show only in the hover tooltip
            datalabels: { display: false },
            tooltip: {
                backgroundColor: "#022352",
                titleFont: { size: 11, weight: "600" },
                bodyFont: { size: 12, weight: "700" },
                padding: 8,
                cornerRadius: 6,
                displayColors: false,
                callbacks: {
                    title: (items) => {
                        const label = items[0]?.label ?? "";
                        return isMinView ? `Time: ${toClock(label)}` : `${active.axis}: ${label}`;
                    },
                    label: (item) => `Avg interval: ${formatDuration(rows[item.dataIndex]?.avgInterval)}`
                }
            }
        },
        scales: {
            x: {
                grid: { display: false },
                border: { color: GRID_COLOR },
                ticks: {
                    color: TICK_COLOR,
                    font: { size: 10 },
                    maxRotation: 0,
                    autoSkipPadding: 12,
                    // minute view: "1:05 PM" style times
                    callback(value) {
                        const label = this.getLabelForValue(value);
                        return isMinView ? toClock(label) : label;
                    }
                }
            },
            y: {
                beginAtZero: true,
                suggestedMax: yMax ? yMax * Y_HEADROOM : undefined,
                grid: { color: GRID_COLOR },
                // solid y-axis line; dash applies to the grid lines
                border: { display: true, color: GRID_COLOR, dash: [4, 4] },
                ticks: {
                    color: TICK_COLOR,
                    font: { size: 10 },
                    maxTicksLimit: 6,
                    callback: (value) => formatYTick(value)
                }
            }
        }
    }), [active, rows, isMinView, yMax]);

    return (
        <div className={`card ${styles.card} mx-3 mt-2 mb-2`}>
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-1">
                <div className={TCUStyles.titleWrap}>
                    <span className={TCUStyles.titleBar} />
                    <span className={TCUStyles.title}>{title}</span>
                    <span className={TCUStyles.subtitle}>{subtitle}</span>
                </div>

                <div className={styles.actions}>
                    <div className={styles.segmented} role="group" aria-label="Trend granularity">
                        {GRANULARITIES.map((g) => (
                            <button
                                key={g.key}
                                type="button"
                                className={`${styles.segmentBtn} ${g.key === granularity ? styles.segmentBtnActive : ""}`}
                                aria-pressed={g.key === granularity}
                                onClick={() => setGranularity(g.key)}
                            >
                                {g.label}
                            </button>
                        ))}
                    </div>

                    <button
                        type="button"
                        className={`${TCUStyles.downloadBtn} custom-Btn-animation`}
                        title="Download latency trend (CSV)"
                        disabled={isLoading || !rows.length}
                        onClick={() =>
                            downloadCsv(LATENCY_DOWNLOAD_APIS.latencyTrend, { ...GlobalFilter }, title).catch(() => { })
                        }
                    >
                        <DownloadIcon size={15} />
                    </button>
                </div>
            </div>

            {/* Min view: hour tabs on a frame; the chart below is the selected tab's panel */}
            <div className={isMinView ? styles.tabFrame : ""}>
                {isMinView && (
                    <div
                        ref={hourTabsRef}
                        className={styles.hourTabs}
                        role="tablist"
                        aria-label="Hour of day"
                        onKeyDown={onHourKeyDown}
                    >
                        {HOURS.map((h) => {
                            const isActive = h.value === selectedHour;
                            return (
                                <button
                                    key={h.value}
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    tabIndex={isActive ? 0 : -1}
                                    disabled={h.value > lastHour}
                                    className={`${styles.hourTab} ${isActive ? styles.hourTabActive : ""}`}
                                    title={h.value > lastHour ? "No data yet" : `Minute-wise latency for ${h.label}`}
                                    onClick={() => setSelectedHour(h.value)}
                                >
                                    {h.label}
                                </button>
                            );
                        })}
                    </div>
                )}

                <div className={isMinView ? styles.tabPanel : ""}>
                    <div className={styles.chartWrap}>
                        {isLoading ? (
                            <ChartLoader />
                        ) : !rows.length ? (
                            <div className={styles.chartState}>No latency data for the selected filters</div>
                        ) : (
                            <Line data={data} options={options} plugins={[crosshairPlugin]} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
